import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface LoaderExclusion {
  exclude: string;
  type: LoaderExclusionType;
}

export enum LoaderExclusionType {
  equals = 1,
  startsWith = 2,
  endsWith = 3,
  contains = 4,
  isStart = 5,
  isEnd = 6,
  isContained = 7
}

export class LoaderStatus {
  public shown = false;
  public message: string | undefined;
  public progress: number | undefined;

  public get hasProgress(): boolean { return typeof this.progress !== 'undefined' && this.progress != null; }
  public get hasMessage(): boolean { return typeof this.progress !== 'undefined' && this.message != null; }
}

@Injectable({
  providedIn: 'root'
})
export class LoaderService {
  private static elist: { [exclude: string]: LoaderExclusionType } = {};

  private counter;
  private status: LoaderStatus;
  private subject: BehaviorSubject<LoaderStatus>;

  constructor() {
    this.counter = 0;
    this.status = new LoaderStatus();
    this.subject = new BehaviorSubject<LoaderStatus>(this.status);
  }

  public static setExclusions(exclusions: LoaderExclusion[]): void {
    for (const e of exclusions) { LoaderService.elist[e.exclude.toLowerCase()] = e.type; }
  }

  public static addExclusion(exclude: string, type?: LoaderExclusionType): void {
    LoaderService.elist[exclude.toLowerCase()] = type ? type : LoaderExclusionType.isContained;
  }

  public static removeExclusion(exclude: string): void {
    exclude = exclude.toLowerCase();
    if (LoaderService.elist[exclude]) { delete LoaderService.elist[exclude]; }
  }

  private static isExcluded(value: string): boolean {
    value = value.toLowerCase();
    for (const e in LoaderService.elist) {
      if (LoaderService.checkExclusion(value, e, LoaderService.elist[e])) { return true; }
    }

    return false;
  }

  private static checkExclusion(value: string, e: string, type: LoaderExclusionType) {
    switch (type) {
      case LoaderExclusionType.equals: return e === value;
      case LoaderExclusionType.contains: return e.indexOf(value) !== -1;
      case LoaderExclusionType.endsWith: return e.endsWith(value);
      case LoaderExclusionType.startsWith: return e.startsWith(value);
      case LoaderExclusionType.isContained: return value.indexOf(e) !== -1;
      case LoaderExclusionType.isStart: return value.startsWith(e);
      case LoaderExclusionType.isEnd: return value.endsWith(e);
      default: return false;
    }
  }

  public showIf(condition: string, message?: string, progress?: number): void {
    if (!(LoaderService.isExcluded(condition))) { this.show(message, progress); }
  }

  public show(message?: string, progress?: number): void {
    this.counter++;
    if (!(this.status.shown)) { return this.emit(true, message, progress); }
    if (this.status.message !== message) { return this.emit(true, message, progress); }
    if (this.status.progress !== progress) { return this.emit(true, message, progress); }
  }

  public hideIf(condition: string, clear?: boolean): void {
    if (!(LoaderService.isExcluded(condition))) { this.hide(clear); }
  }

  public hide(clear?: boolean): void {
    if (clear || (--this.counter <= 0)) {
      this.counter = 0;
      this.emit(false);
    }
  }

  public progress(progress: number): void {
    if (this.status.shown && (this.status.progress !== progress)) { this.emit(true, this.status.message, progress); }
  }

  public message(message: string): void {
    if (this.status.shown && (this.status.message !== message)) { this.emit(true, message, this.status.progress); }
  }

  public subscribe(next: (status: LoaderStatus) => void): void {
    this.subject.subscribe(next);
  }

  private emit(shown: boolean, message?: string, progress?: number) {
    this.status.shown = shown;
    this.status.message = message;
    this.status.progress = progress;
    this.subject.next(this.status);
  }
}
